import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;
/** Deferred section hydration can inject submenu content after opening; stop watching after this long. */
const SUBMENU_OBSERVE_MS = 500;
/**
 * pointerleave on the old item fires before pointerenter on the next one. Closing is deferred so moving between
 * items switches panels without collapsing the header, and a brief slip off the menu does not drop it.
 */
const CLOSE_DELAY_MS = 120;

/**
 * A custom element that manages a header menu.
 *
 * Only one item is ever open: opening an item always fully closes the previous one (aria-expanded, `is-active`,
 * submenu `data-active`, pointer tracking), whether or not a pointerleave/blur reached it first.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {HTMLElement | null} */
  #activeItem = null;

  /** @type {MutationObserver | null} */
  #submenuObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #submenuObserverTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #pointerIdleTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  /** Last known pointer position for Safari hit-test reconciliation. */
  #lastPointer = { x: 0, y: 0 };

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    if (this.#activeItem) this.#resetItem(this.#activeItem);
    this.#activeItem = null;
    this.#stopPointerMoveListener();
    this.#cancelHoverDispatch();
    this.#cancelClose();
    this.#cleanupSubmenuObserver();
  }

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#close();
  };

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /** Whether the overflow list is hovered. */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Open the menu item under the event target.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    const header = this.headerComponent;
    if (!(event.target instanceof Element) || !header) return;

    const item = findMenuItem(event.target);
    if (!item) return;
    this.#cancelClose();
    if (item === this.#activeItem) return;

    const isDefaultSlot = event.target.slot === '';
    const previousItem = this.#activeItem;

    // Header heights stay as they are so the clip-path animates from the old submenu height to the new one.
    if (previousItem) this.#resetItem(previousItem);

    this.#activeItem = item;
    this.dataset.overflowExpanded = String(!isDefaultSlot);
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    item.parentElement?.classList.add('is-active');

    const ownSubmenu = findSubmenu(item);
    const submenu = ownSubmenu ?? (isDefaultSlot ? null : this.overflowMenu);

    if (submenu) {
      submenu.dataset.active = '';
      this.#scheduleHoverDispatch(item, event instanceof FocusEvent);
      this.#watchSubmenuHeight(submenu);
    }

    let submenuHeight = submenu?.offsetHeight ?? 0;
    // Overflow: the panel is as tall as either the hovered submenu or the list of overflowing links.
    if (submenu && !isDefaultSlot) {
      const linksHeight = this.#getOverflowListLinksHeight();
      submenuHeight = ownSubmenu ? Math.max(this.overflowMenu?.offsetHeight ?? 0, linksHeight) : linksHeight;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();
    header.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.#setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');

    if (!previousItem) document.body.addEventListener('pointermove', this.#onPointerMove);
    this.#setSafetyBox(item);
  };

  /**
   * Close the active item when the pointer or focus leaves it.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element) || !this.#activeItem) return;

    // A late leave from the previous item must not close the one that just opened.
    const leftItem = event.target.closest('.menu-list__list-item');
    if (leftItem && !leftItem.matches('[slot="more"]') && !leftItem.contains(this.#activeItem)) return;

    const related = event.relatedTarget instanceof Node ? event.relatedTarget : null;
    // Chrome fires pointerleave on a slotted list item when the pointer moves onto its own link.
    if (event.type === 'pointerleave' && related && leftItem?.contains(related)) return;

    const submenu = findSubmenu(this.#activeItem);
    // Keyboard: tabbing from the top-level link into its own submenu keeps it open.
    const focusIntoSubmenu = event.type === 'blur' && related !== null && Boolean(submenu?.contains(related));
    const toOverflowMenu = related?.parentElement?.matches('[slot="overflow"]') ?? false;

    if (focusIntoSubmenu || toOverflowMenu) {
      this.#clearSafetyBox(this.#activeItem);
      return;
    }

    this.#cancelClose();
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = undefined;
      this.#close();
    }, CLOSE_DELAY_MS);
  }

  #cancelClose() {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
  }

  /** Close the active item and collapse the header. */
  #close = () => {
    const item = this.#activeItem;
    if (!item) return;

    // The overflow panel lives in a shadow root; leaving an item inside it must not close it while it is hovered.
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.#resetItem(item);
    this.#activeItem = null;
    this.#stopPointerMoveListener();

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.ariaExpanded = 'false';
  };

  /**
   * Remove every open-state marker from one item.
   * @param {HTMLElement} item
   */
  #resetItem(item) {
    this.#cancelHoverDispatch();
    this.#cleanupSubmenuObserver();
    this.#clearSafetyBox(item);

    item.ariaExpanded = 'false';
    item.parentElement?.classList.remove('is-active');

    const submenu = findSubmenu(item);
    if (submenu) delete submenu.dataset.active;
    if (this.overflowMenu) delete this.overflowMenu.dataset.active;
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} immediate
   */
  #scheduleHoverDispatch(item, immediate) {
    this.#cancelHoverDispatch();
    if (immediate) {
      this.dispatchEvent(new MegaMenuHoverEvent());
      return;
    }
    this.#hoverDispatchTimer = setTimeout(() => {
      this.#hoverDispatchTimer = undefined;
      if (this.#activeItem === item) this.dispatchEvent(new MegaMenuHoverEvent());
    }, HOVER_COMMIT_DELAY_MS);
  }

  #cancelHoverDispatch() {
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
  }

  /**
   * Re-measure once deferred content lands so the header does not stay at contain-intrinsic-size.
   * @param {HTMLElement} submenu
   */
  #watchSubmenuHeight(submenu) {
    this.#cleanupSubmenuObserver();
    this.#submenuObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (submenu.offsetHeight > 0) {
            this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
            this.#cleanupSubmenuObserver();
          }
        });
      });
    });
    this.#submenuObserver.observe(submenu, { childList: true, subtree: true });
    this.#submenuObserverTimer = setTimeout(() => this.#cleanupSubmenuObserver(), SUBMENU_OBSERVE_MS);
  }

  #cleanupSubmenuObserver() {
    this.#submenuObserver?.disconnect();
    this.#submenuObserver = null;
    clearTimeout(this.#submenuObserverTimer);
    this.#submenuObserverTimer = undefined;
  }

  /**
   * Safety box: while the pointer is moving, the active link's hit area extends down to the submenu so a diagonal
   * move toward the panel does not cross a neighbouring link.
   * @param {HTMLElement} item
   */
  #setSafetyBox(item) {
    const header = this.headerComponent;
    const boundary = header?.hasAttribute('data-submenu-overlap-bottom-row')
      ? header.querySelector('.header__row--top')
      : header;
    const top = item.getBoundingClientRect().top;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - top}px`);
  }

  /** @param {HTMLElement} item */
  #clearSafetyBox(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  #stopPointerMoveListener() {
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
  }

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const item = this.#activeItem;
    if (!item) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    item.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (!moving) {
      this.#reconcilePointerTarget();
      return;
    }
    this.#pointerIdleTimer = setTimeout(() => {
      if (!this.#activeItem) return;
      this.#activeItem.dataset.safetyBox = 'false';
      this.#reconcilePointerTarget();
    }, 50);
  };

  /** Safari does not re-hit-test after the safety box pseudo-element shrinks; open the item actually under the pointer. */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const listItem = document.elementFromPoint(x, y)?.closest('.menu-list__list-item');
      if (listItem && this.#activeItem && !listItem.contains(this.#activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  #getOverflowListLinksHeight() {
    const overflow = this.overflowMenu;
    const slottedMenuLinks = overflow?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return overflow?.offsetHeight ?? 0;

    const submenus = slottedMenuLinks
      .map((link) => link.querySelector('[ref="submenu[]"]'))
      .filter((submenu) => submenu instanceof HTMLElement);

    submenus.forEach((submenu) => submenu.style.setProperty('display', 'none'));
    const height = overflow?.offsetHeight ?? 0;
    submenus.forEach((submenu) => submenu.style.removeProperty('display'));
    return height;
  }

  /** Read the visible header height before submenu height writes invalidate layout. */
  #getHeaderVisibleHeight() {
    const header = this.headerComponent;
    if (!header) return 0;

    if (header.hasAttribute('data-submenu-overlap-bottom-row') && header.offsetHeight > 0) {
      return /** @type {HTMLElement | null} */ (header.querySelector('.header__row--top'))?.offsetHeight ?? 0;
    }
    return header.offsetHeight;
  }

  /**
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    const fullOpenHeaderHeight = submenuHeight === 0 ? 0 : submenuHeight + headerVisibleHeight;
    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  #preloadImages = () => {
    this.querySelectorAll('img[loading="lazy"]').forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the menu link for a list item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  // Hovering "More" opens the first overflowing item.
  if (element.matches('[slot="more"]')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element.querySelector('[ref="menuitem"]');
}

/**
 * Find the submenu that belongs to a menu link.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
